import * as v from "valibot";
import {
  CallResponseSchema,
  OkResponseSchema,
  StartCallResponseSchema,
} from "../../shared/api";
import { IceCandidateSchema, IceResponseSchema } from "../../shared/schemas";
import { api, requestApi } from "../data/api";
import { realtime } from "../data/realtime";
import { report, selectedTransport } from "../data/telemetry";
import { sounds } from "../sounds/sounds";
import type { CallPorts } from "./call-session";
import { applyChosenSpeaker, captureMedia, chosenDevices } from "./devices";
import { watchVoiceLevel } from "./voice-level";

const cameraQuality: MediaTrackConstraints = {
  width: { ideal: 1280, max: 1280 },
  height: { ideal: 720, max: 720 },
  frameRate: { ideal: 24, max: 30 },
};
function cameraConstraints(): MediaTrackConstraints {
  const { camera } = chosenDevices();
  return {
    ...cameraQuality,
    ...(camera ? { deviceId: { ideal: camera } } : { facingMode: "user" }),
  };
}

const timeout = (ms?: number) => (ms ? AbortSignal.timeout(ms) : undefined);
const iceRequestTimeout = 10000;

export const browserCallPorts: CallPorts = {
  createPeer: (config) => new RTCPeerConnection(config),
  signaling: {
    connected: () => realtime.connected,
    subscribe: (listener) => realtime.subscribe(listener),
    readCall: (id, timeoutMs) =>
      api(
        CallResponseSchema,
        undefined,
        `?call=${encodeURIComponent(id)}`,
        timeout(timeoutMs),
      ),
    iceServers: async (personId, timeoutMs) =>
      (
        await requestApi(
          `/api/ice?peer=${encodeURIComponent(personId)}`,
          IceResponseSchema,
          undefined,
          timeout(timeoutMs),
        )
      ).iceServers,
    placeCall: (peer, media, offer) =>
      api(StartCallResponseSchema, {
        action: "call",
        peer,
        media,
        supportsScreenSharing: true,
        offer: JSON.stringify(offer),
      }),
    answerCall: async (callId, answer) => {
      await api(OkResponseSchema, {
        action: "answer",
        callId,
        answer: JSON.stringify(answer),
      });
    },
    endCall: async (callId) => {
      await api(OkResponseSchema, { action: "end", callId });
    },
    endCallOnLeave: (callId) =>
      void navigator.sendBeacon(
        "/api/app",
        new Blob([JSON.stringify({ action: "end", callId })], {
          type: "application/json",
        }),
      ),
    requestRestart: async (callId, revision) => {
      await api(
        OkResponseSchema,
        { action: "restart", callId, revision },
        "",
        timeout(iceRequestTimeout),
      );
    },
    sendRestartDescription: async (callId, revision, description) => {
      const common = { callId, revision };
      await api(
        OkResponseSchema,
        description.type === "offer"
          ? {
              ...common,
              action: "restart-offer",
              offer: JSON.stringify(description),
            }
          : {
              ...common,
              action: "restart-answer",
              answer: JSON.stringify(description),
            },
        "",
        timeout(iceRequestTimeout),
      );
    },
    sendCandidate: async (callId, candidate, revision = 0) => {
      const parsed = v.parse(IceCandidateSchema, candidate);
      if (
        !realtime.send({ type: "signal", callId, candidate: parsed, revision })
      )
        await api(OkResponseSchema, {
          action: "ice",
          callId,
          candidate: parsed,
          revision,
        });
    },
    announceAlive: (callId) =>
      void realtime.send({ type: "call-alive", callId }),
  },
  devices: {
    captureCall: (camera) => captureMedia(camera && cameraConstraints()),
    captureCamera: (choice) =>
      navigator.mediaDevices.getUserMedia({
        audio: false,
        video: choice ? { ...cameraQuality, ...choice } : cameraConstraints(),
      }),
    captureScreen: () =>
      navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 15, max: 30 },
        },
        audio: false,
      }),
    canShareScreen: () =>
      typeof navigator.mediaDevices?.getDisplayMedia === "function",
    listCameras: async () =>
      (await navigator.mediaDevices.enumerateDevices()).filter(
        (device) => device.kind === "videoinput",
      ),
    watchDevices: (listener) => {
      const devices = navigator.mediaDevices;
      devices?.addEventListener("devicechange", listener);
      return () => devices?.removeEventListener("devicechange", listener);
    },
    stream: (tracks) => new MediaStream(tracks),
    watchLevel: watchVoiceLevel,
    speaker: (heard, muted) => {
      const output = new Audio();
      output.autoplay = true;
      output.muted = muted;
      output.srcObject = heard;
      applyChosenSpeaker(output);
      return output;
    },
  },
  page: {
    hidden: () => document.hidden,
    online: () => navigator.onLine,
    listen: ({ network, visibility, leave }) => {
      const connection = (
        navigator as Navigator & {
          connection?: EventTarget & { type?: string };
        }
      ).connection;
      let networkType = connection?.type;
      const networkChanged = () => {
        // NetworkInformation also reports ordinary bandwidth/latency changes.
        // Restart proactively only for a connection-type change; ICE state events
        // detect address changes on browsers that do not expose that type.
        const next = connection?.type;
        if (next && next !== networkType) network();
        networkType = next;
      };
      window.addEventListener("online", network);
      window.addEventListener("offline", network);
      connection?.addEventListener("change", networkChanged);
      document.addEventListener("visibilitychange", visibility);
      window.addEventListener("pagehide", leave);
      return () => {
        window.removeEventListener("online", network);
        window.removeEventListener("offline", network);
        connection?.removeEventListener("change", networkChanged);
        document.removeEventListener("visibilitychange", visibility);
        window.removeEventListener("pagehide", leave);
      };
    },
    confirmLeaving: () => {
      const confirm = (event: BeforeUnloadEvent) => event.preventDefault();
      window.addEventListener("beforeunload", confirm);
      return () => window.removeEventListener("beforeunload", confirm);
    },
  },
  sounds,
  telemetry: { report, selectedTransport },
};
